import { useEffect } from 'react';

import { Flex, FloraButton, Toaster } from '@grupoboticario/flora-react';
import { CalendarCheckIcon, SquareArrowRightUpIcon } from '@grupoboticario/flora-react-icons';

import { ResponsiveFloraTypography } from 'components';
import { useIncentiveCampaign, useResellerDetail } from 'hooks';
import { getInvalidMemberFields, isInvalidChallengeMember } from 'pages/IncentiveCampaign/helpers';
import { useLoyaltyIncentiveCampaign } from 'pages/IncentiveCampaign/hooks';

import Styles from './RedeemPrizes.styles';

export const RedeemPrizes = () => {
  const { sso, accumulated } = useIncentiveCampaign;
  const { campaignId } = useLoyaltyIncentiveCampaign();
  const { data: challengeData } = accumulated.useQuery(campaignId);
  const { mutate: ssoMutate, data, isLoading, isSuccess, isError } = sso.useMutation();
  const points = challengeData?.points ?? 0;
  const isInvalidMember = isInvalidChallengeMember(challengeData?.validRegistrationFields);
  const invalidFields = getInvalidMemberFields(challengeData?.validRegistrationFields);
  const { data: resellerDetail } = useResellerDetail();
  const isLoyaltyMember = resellerDetail?.reseller.isLoyaltyMember ?? false;

  const handleSSOClick = () => {
    ssoMutate({});
  };

  const navigateToProfile = () => {
    window.open('/perfil', '_blank', 'noopener, noreferrer');
  };

  useEffect(() => {
    if (isError) {
      Toaster.notify({
        kind: 'error',
        description:
          'Não foi possível acessar a página de resgate de prêmios. Por favor, tente novamente.',
        origin: 'right-bottom',
      });
      return;
    }
    if (isSuccess && data?.success && data.ssoUrl) {
      window.open(data.ssoUrl, '_blank', 'noopener, noreferrer');
    }
  }, [isError, isSuccess, data]);

  if (isInvalidMember) {
    return (
      <Flex direction='column' gap='$4'>
        <Styles.LinkCard>
          <Styles.CardInfo>
            <Flex direction='column' gap='$2'>
              <Flex>
                <Styles.InfoIcon>
                  <CalendarCheckIcon size={28} color='$nonInteractiveAuxiliar' />
                </Styles.InfoIcon>
                <ResponsiveFloraTypography
                  color='$nonInteractivePredominant'
                  fontWeight='bold'
                  fontSize='subtitle'
                >
                  Campanha encerrada!
                </ResponsiveFloraTypography>
              </Flex>

              {!isLoyaltyMember && (
                <Styles.Message>
                  <ResponsiveFloraTypography
                    color='$nonInteractivePredominant'
                    fontWeight='bold'
                    fontSize='subtitle'
                  >
                    Atenção: Se você acumulou pontos, atualize seus dados cadastrais para conseguir
                    resgatar prêmios.
                  </ResponsiveFloraTypography>

                  <ResponsiveFloraTypography
                    fontSize='bodyLargeShort'
                    color='$nonInteractivePredominant'
                  >
                    Acesse o perfil e atualize os seus seguintes dados:
                  </ResponsiveFloraTypography>
                  <Styles.Items>
                    {invalidFields.map((field, index) => {
                      return (
                        <ResponsiveFloraTypography
                          key={index}
                          fontSize='bodyLargeShort'
                          fontWeight='bold'
                          color='$nonInteractivePredominant'
                        >
                          • {field}
                        </ResponsiveFloraTypography>
                      );
                    })}
                  </Styles.Items>

                  <ResponsiveFloraTypography
                    fontSize='bodyLargeShort'
                    color='$nonInteractivePredominant'
                  >
                    Você tem até 31/03/2025 para resgatar seus pontos.
                  </ResponsiveFloraTypography>
                  <ResponsiveFloraTypography
                    fontSize='bodyLargeShort'
                    color='$nonInteractivePredominant'
                  >
                    Após atualizar, aguarde até 48 horas para que seu acesso ao resgate esteja
                    disponível.
                  </ResponsiveFloraTypography>
                </Styles.Message>
              )}
            </Flex>
          </Styles.CardInfo>
          {!isLoyaltyMember && (
            <Flex direction='column' gap='$4'>
              <FloraButton
                onClick={navigateToProfile}
                autotagging-flora='on'
                data-flora='button'
                icon={<SquareArrowRightUpIcon size={16} />}
                has={'iconLeft'}
                data-flora-text='acessar-perfil'
              >
                <ResponsiveFloraTypography fontWeight='bold'>
                  Acessar perfil
                </ResponsiveFloraTypography>
              </FloraButton>
              <FloraButton
                isLoading={isLoading}
                disabled={isLoading || isInvalidMember}
                onClick={handleSSOClick}
                autotagging-flora='on'
                data-flora='button'
                icon={<SquareArrowRightUpIcon size={16} />}
                has={'iconLeft'}
                data-flora-text='resgatar-premios'
                hierarchy='secondary'
              >
                <ResponsiveFloraTypography fontWeight='bold'>
                  Resgatar prêmios
                </ResponsiveFloraTypography>
              </FloraButton>
            </Flex>
          )}
        </Styles.LinkCard>
      </Flex>
    );
  }

  return (
    <Flex direction='column' gap='$4'>
      <Styles.LinkCard>
        <Styles.CardInfo>
          <Styles.InfoIcon>
            <CalendarCheckIcon size={28} color='$nonInteractiveAuxiliar' />
          </Styles.InfoIcon>
          <Flex direction='column' gap='$2'>
            <ResponsiveFloraTypography
              color='$nonInteractivePredominant'
              fontWeight='bold'
              fontSize='subtitle'
            >
              Campanha encerrada!
            </ResponsiveFloraTypography>
            {!isLoyaltyMember && (
              <>
                <ResponsiveFloraTypography color='$nonInteractivePredominant' fontWeight='bold'>
                  Se você conquistou a partir do desafio 1, utilize seus pontos e aproveite seus
                  prêmios.
                </ResponsiveFloraTypography>
                <ResponsiveFloraTypography
                  fontSize='bodyLargeShort'
                  color='$nonInteractiveAuxiliar'
                >
                  Você tem até 31/03/2025 para usar seus pontos.
                </ResponsiveFloraTypography>
              </>
            )}
          </Flex>
        </Styles.CardInfo>
        {points == 0 || isLoyaltyMember ? (
          <></>
        ) : (
          <FloraButton
            isLoading={isLoading}
            disabled={isLoading || isInvalidMember}
            onClick={handleSSOClick}
            autotagging-flora='on'
            data-flora='button'
            icon={<SquareArrowRightUpIcon size={16} />}
            has={'iconLeft'}
            data-flora-text='resgatar-premios'
          >
            <ResponsiveFloraTypography fontWeight='bold'>
              Resgatar prêmios
            </ResponsiveFloraTypography>
          </FloraButton>
        )}
      </Styles.LinkCard>
    </Flex>
  );
};
